import React, { useEffect, useMemo, useState } from "react";

import { Box } from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import { getBusinessODPConfig } from "api/business-user-management";
import { IBusinessODPConfig } from "api/business-user-management/types";
import { BusinessContentItem } from "features/business-compliance/selected-business/BusinessContentItem";
import { IBusinessContentItem } from "features/business-compliance/selected-business/types";
import { useParams } from "react-router-dom";
import { formatDayWithOrdinals } from "utils";
import { Maybe } from "utils/general/maybe";

import { customErrorToast } from "ui/custom-toast";
import { Spinner } from "ui/spinner";

import { ODPSettings } from "./ODPSettings";
import { ODP_CUT_OFF_VALUE, businessODPConfigList } from "./consts";

const DEFAULT_ODP_CONFIG_SETTINGS: IBusinessODPConfig = {
  id: "",
  paymentFee: "",
  withdrawalPercent: null,
  payrollCutOff: null,
  payrollCutoffDate: null,
  autoWithdrawals: false,
  rollOver: false,
};

export const ODP = () => {
  const { businessId } = useParams();
  const { isPending, data, mutateAsync } = useMutation({
    mutationFn: getBusinessODPConfig,
  });
  const [odpConfig, setOdpConfig] = useState<IBusinessODPConfig>(
    DEFAULT_ODP_CONFIG_SETTINGS
  );

  const handleGetBusinessOdpConfig = async (businessId: string) => {
    const { error, message } = await mutateAsync(businessId);
    if (error) {
      customErrorToast({
        message,
      });
    }
  };

  const odpConfigSettings: IBusinessContentItem[] = useMemo(() => {
    if (!odpConfig?.paymentFee && !odpConfig?.withdrawalPercent)
      return businessODPConfigList;
    const {
      paymentFee,
      withdrawalPercent,
      payrollCutOff,
      autoWithdrawals,
      rollOver,
    } = odpConfig;
    return [
      {
        title: "Salary Date",
        content: payrollCutOff
          ? `${formatDayWithOrdinals(+payrollCutOff)} of every month`
          : "N/A",
      },
      {
        title: "Who pays withdrawal fee?",
        content: paymentFee || "N/A",
      },
      {
        title: "Withdrawal Setup",
        content: autoWithdrawals ? "Allow Auto Withdrawal" : "Require Approval",
      },
      {
        title: "Employee Access",
        content: withdrawalPercent ? `${withdrawalPercent}%` : "N/A",
      },
      {
        title: "Cut-Off Date",
        content: payrollCutOff
          ? `${formatDayWithOrdinals(+payrollCutOff - ODP_CUT_OFF_VALUE)} of every month`
          : "N/A",
      },
      {
        title: "Activate Roll Over",
        content: rollOver ? "True" : "False",
      },
    ];
  }, [odpConfig]);

  useEffect(() => {
    if (!businessId) return;
    void handleGetBusinessOdpConfig(businessId);
  }, [businessId]);

  useEffect(() => {
    if (!data?.data) return;
    setOdpConfig(data.data);
  }, [data]);

  // useEffect(() => console.log("data: ", data), [data]);

  return (
    <Box
      className="bg-white mt-1 p-6 rounded-[20px] flex flex-col gap-4"
      css={{ boxShadow: "0px 0px 5px 0px #0000000D" }}
    >
      <ODPSettings
        isLoadingOdpSettings={isPending || !data}
        handleGetBusinessOdpConfig={handleGetBusinessOdpConfig}
        odpConfig={odpConfig}
        setOdpConfig={setOdpConfig}
      />
      <Maybe condition={isPending}>
        <Box className="w-full flex items-center justify-center h-16">
          <Spinner size="40px" />
        </Box>
      </Maybe>
      <Maybe condition={!isPending}>
        <Box
          css={{
            display: "flex",
            flexWrap: "wrap",
            columnGap: "32px",
            rowGap: "16px",
            width: "100%",
            maxWidth: "78%",
          }}
        >
          {odpConfigSettings.map(({ title, content }, index) => (
            <BusinessContentItem
              key={index}
              title={title}
              content={content}
              isContentWrapperBelow745={false}
              isContentWrapperBelow600={false}
              busienssContentItemHeaderClassName="whitespace-nowrap"
            />
          ))}
        </Box>
      </Maybe>
    </Box>
  );
};
